import { DEFAULT_THEME, type MantineTheme } from '@mantine/core';
import { Inter } from 'next/font/google';
import localFont from 'next/font/local';

import { HEADER_HEIGHT } from '~/domains/common/components/AppShellHeader/constants';

import { GRAY_COLORS, SPLASH_COLORS } from './const';
import { generateColors } from './themeUtils';

const inter = Inter({ subsets: ['latin'], fallback: ['sans-serif'] });
const hubotSans = localFont({
  src: '../../public/fonts/Hubot-Sans.woff2',
  variable: '--mantine-hubot-sans',
});
const hubotSansBlack = localFont({
  src: '../../public/fonts/Hubot-Sans-Black.ttf',
  variable: '--mantine-hubot-sans-black',
});
const hubotSansExpanded = localFont({
  src: '../../public/fonts/Hubot-Sans-Expanded-ExtraBold.ttf',
  variable: '--mantine-hubot-sans-expanded',
});
// For swatch generation, check out https://smart-swatch.netlify.app/

const baseTheme: MantineTheme = {
  ...DEFAULT_THEME,
  ...inter?.style,
  colorScheme: 'light',
  primaryColor: 'splash',
  primaryShade: { light: 4, dark: 3 },
  cursorType: 'pointer',
  // Default border-radius used for most elements
  defaultRadius: 4,
  // White and black colors, defaults to '#fff' and '#000'
  black: '#101113',
  white: '#fff',
  colors: {
    splash: SPLASH_COLORS,
    gray2: [
      '#f1f3f5',
      '#d1d1d1',
      '#c1c2c5',
      '#909296',
      '#495057',
      '#25262b',
      '#1a1c1e',
      '#101113',
      '#101113',
      '#101113',
    ],
    ...DEFAULT_THEME.colors,
    gray: GRAY_COLORS,
    // gray.1, gray.2, gray.3, gray.4, gray.5, gray.6, -, -, -
    red: generateColors(['#ED9D9D', '#DB3B3B', '#891C1C']),
    blue: generateColors(['#A5F5F1', '#4BEBE2', '#1BC4CF']),
    green: generateColors(['#B2F2BB', '#40C057', '#2B8A3E']),
    orange: generateColors(['#F5B094', '#EB612A', '#7C3518']),
    pink: generateColors(['#EDA3FB', '#DA47F7', '#7F1A93']),
    violet: generateColors(['#B4A6EF', '#6A4EDE', '#2D2057']),
    ofpBlue: generateColors(['#163e67']),
    gold: generateColors(['#E88913', '#CA9552', '#FFF8E4']),
  },
  other: {
    fontFamily: {
      hubotSans: `var(--mantine-hubot-sans)`,
      hubotSansBlack: `var(--mantine-hubot-sans-black)`,
      hubotSansExpanded: `var(--mantine-hubot-sans-expanded)`,
    },
    screenWidth: {
      lg: '1120px',
      xl: '1440px',
    },
    zIndex: {
      aeropayWidget: 9000,
      postPickModal: 1100,
      baseLayoutFooter: 999,
      deleteCardModal: 301,
      cryptoAddressSheet: 302,
      dialogModal: 201,
      headerMenu: 150,
      mobileMenu: 150,
      marketplacePanel: 150,
      // The design-system MultiSelectFilter mobile sheet portals to document.body, so its z-index is
      // a global layer, not sibling-local: it must clear the sticky header (100) while staying under
      // the side panel (150) and any modal (201).
      filterSheet: 120,
      header: 100,
      footer: 100,
      subHeader: 99,
      tooltip: {
        default: 99,
      },
      referAFriendDrawer: 100,
      marketPanel: 100,
      headerDrawer: 90,
      marketDisclaimer: 85,
      dropdownFlyout: 80,
      // Local stacking — these order siblings inside one component's own stacking context, not
      // global layers (styling.md: every z-index comes from the registry, sibling-only ones too).
      stickyHeaderCell: 1,
      stickyFrozenColumn: 2,
      filterMenu: 2,
      stickySaveBar: 1,
      entrySlipSheet: 3,
      liveTrackerBubble: 1,
      cardStretchedLink: 1,
      cardInnerLink: 2,
      scrollerControl: 1,
      modal: {
        header: 1000,
      },
    },
    shadow: {
      card: '0px 2px 20px 0px rgba(0, 0, 0, 0.06)',
      flyout: '0px 8px 40px 0px rgba(0, 0, 0, 0.10)',
      slip: '0px 0px 24px 0px rgba(0, 0, 0, 0.10)',
    },
    gradient: {
      // Marketplace dark panel header.
      darkHeader: 'linear-gradient(180deg, #4C4C4C 0%, #121212 100%)',
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.3)',
      backdropFilter: 'blur(10px)',
    },
    layout: {
      maxWidth: 1440,
      paddingLg: 24,
      paddingMd: 20,
      paddingSm: 10,
    },
    spacing: {
      _2: 2,
      _4: 4,
      _6: 6,
      _8: 8,
      _12: 12,
      _16: 16,
      _24: 24,
      _32: 32,
      _40: 40,
      _48: 48,
      _64: 64,
    },
    // One shape for every marketplace action button; 10 is not on the theme radius scale, so it is
    // defined here rather than picked.
    actionButton: {
      radius: 10,
      paddingVertical: 10,
      paddingHorizontal: 16,
    },
    card: {
      radius: 12,
    },
    // The marketplace primary-fill CTA shape. No radius key: the corner radius is
    // actionButton.radius, shared so both marketplace CTA shapes round to the same corner.
    primaryCta: {
      paddingHorizontal: 16,
    },
    color: {
      blue: {
        hover: '#3BDCD3',
      },
      gray: {
        segments: '#E5E7E9',
        hover: '#DFE2E4',
      },
      marketplace: {
        gradientFrom: '#6A4EDE',
        gradientTo: '#4B34A9',
      },
    },
  },

  breakpoints: {
    ...DEFAULT_THEME.breakpoints,
    xs: 480, // <-- "mobile"
  },

  globalStyles: (theme) => ({
    ':root': {
      // new AppShellHeader is not using Header from mantine, thus we need to set this variable manually, because there are many dependencies on it
      '--mantine-header-height': `${HEADER_HEIGHT}px`,
      '--mantine-hubot-sans': hubotSans.style.fontFamily,
      '--mantine-hubot-sans-black': hubotSansBlack.style.fontFamily,
      '--mantine-hubot-sans-expanded': hubotSansExpanded.style.fontFamily,
    },

    '*, *::before, *::after': {
      boxSizing: 'border-box',
    },

    body: {
      ...theme.fn.fontStyles(),
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
      lineHeight: theme.lineHeight,
    },

    // full height
    'html, body, #__next, #storybook-root': {
      height: '100%',
    },

    // Hide OSANO cookie widget button
    '.osano-cm-widget': {
      display: 'none !important',
    },

    // Osano "opt-out honored" pill (bottom-left) + its tooltip. Behaviour
    // partner: domains/common/app/optOutNotice.ts. Distinct from the
    // .osano-cm-widget cookie tab hidden above.
    //
    // All rules target the FLOATING pill only (`osano-cm-window__…`). The base
    // `osano-cm-opt-out-honored-wrapper` class is ALSO on the in-dialog "opt-out
    // honored" notice, which the user opens deliberately — it must not fade or be
    // dismissed out from under them.
    //
    // 1. Auto-fade to invisible 10s after Osano paints the pill; visibility:hidden
    //    also drops it out of the click/tab path. :hover / :focus-within /
    //    --tooltip-active (on the same element) pause the countdown so the injected
    //    "Hide for N days" CTA stays reachable and a focused control is never
    //    hidden (WCAG).
    // 2. optOutNotice.ts sets .splash-opt-out-notice-hidden on <html> from a
    //    localStorage expiry. The display:none rule ships in this stylesheet
    //    before Osano paints, but the CLASS is applied client-side after
    //    hydration — a returning (already-dismissed) user can briefly see the
    //    pill if Osano paints before that effect runs.
    '@keyframes osano-cm-opt-out-fade': {
      to: { opacity: 0, visibility: 'hidden' },
    },
    '.osano-cm-window__opt-out-honored-wrapper': {
      animation: 'osano-cm-opt-out-fade 0.6s ease 10s forwards',
    },
    '.osano-cm-window__opt-out-honored-wrapper:hover, .osano-cm-window__opt-out-honored-wrapper:focus-within, .osano-cm-window__opt-out-honored-wrapper.osano-cm-opt-out-honored-wrapper--tooltip-active':
      {
        animationPlayState: 'paused',
      },
    'html.splash-opt-out-notice-hidden .osano-cm-window__opt-out-honored-wrapper': {
      display: 'none !important',
    },
    // The "Hide for N days" CTA optOutNotice.ts injects into Osano's tooltip.
    // align-self/text-align pin it left inside Osano's centering flex column.
    '.splash-opt-out-notice__hide': {
      display: 'block',
      alignSelf: 'flex-start',
      margin: '0.75rem 0 0',
      padding: 0,
      border: 0,
      background: 'none',
      color: 'inherit',
      font: 'inherit',
      fontSize: '0.8125rem',
      textAlign: 'left',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    // Guarantee a visible focus ring — the bare button can't rely on Osano's reset.
    '.splash-opt-out-notice__hide:focus-visible': {
      outline: '2px solid currentColor',
      outlineOffset: '2px',
    },

    ...((global as { Cypress?: any }).Cypress &&
      process.env.NEXT_PUBLIC_ENV !== 'production' && {
        '.osano-cm-dialog': {
          display: 'none',
        },
      }),

    // hide recaptcha badge by https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
    '.grecaptcha-badge': {
      visibility: 'hidden',
    },
  }),
};

export const mainTheme: MantineTheme = {
  ...baseTheme,
};

export const lightTheme: MantineTheme = {
  ...baseTheme,
  colorScheme: 'light',
};
